import { ResourceSessionStatus, ResourceType } from '@/lib/shared-types';
import { api } from './api';
import { unwrapList } from './guest';

/**
 * The ONE place the guest resource-session route is written down.
 *
 * The backend is growing this endpoint alongside this screen and the final path
 * is not settled yet, so every caller goes through this constant: swapping it is
 * a one-line change here rather than a hunt through the component, its test and
 * whatever else grew a copy in the meantime.
 *
 * No `sessionId` is sent with it, deliberately. The guest token names exactly
 * one visit at exactly one table and the server resolves the table from it —
 * the same rule `GuestScopeGuard` applies to `/checks` — so there is no
 * parameter here for a guest to edit into someone else's meter.
 */
export const GUEST_RESOURCE_SESSIONS_PATH = '/resource-sessions';

/**
 * A meter running on the guest's own table — a PlayStation, a billiards table,
 * an hour in the kids' area — as the guest-readable endpoint describes it.
 *
 * This is the whole of what a guest may see, and the omissions are the point:
 * no child or guardian, no entitlement, no pricing rule, no check id and no
 * staff id. The API does not send them by design, so nothing here can render
 * them by accident.
 */
export interface GuestResourceSession {
  id: string;
  resourceName: string;
  /** A `ResourceType` value, but read as a string — see {@link resourceTypeLabelKey}. */
  resourceType: string;
  /** A `ResourceSessionStatus` value, read as a string for the same reason. */
  status: string;
  startedAt: string | null;
  elapsedSeconds: number;
  /**
   * A decimal STRING, never a float.
   *
   * The server computes it with decimal.js and the till prints that same
   * figure. Parsing it into a Number here to do anything at all with it is how
   * a total on a guest's phone ends up a penny away from the total on the
   * receipt, so it is carried as text from the wire to `formatCurrency` and
   * touched by nothing in between.
   */
  accruedAmount: string;
  /** ISO 4217, stamped per session — the meter is denominated by the branch. */
  currency: string;
}

/** One reading of the table's meters, and the instant it was taken. */
export interface ResourceSessionsSnapshot {
  sessions: GuestResourceSession[];
  /** Epoch ms, so elapsed can advance locally between polls. See {@link elapsedSecondsAt}. */
  fetchedAt: number;
}

type Dict = Record<string, unknown>;

const asDict = (value: unknown): Dict =>
  value && typeof value === 'object' ? (value as Dict) : {};

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

/**
 * Read one row the way `guest.ts` reads the session: defensively, because a
 * build of this app sits in a guest's browser cache long after the API changes
 * shape. A row missing its id or its name is dropped rather than rendered — a
 * card reading "undefined" beside a live charge is worse than one fewer card.
 */
function normalizeSession(raw: unknown): GuestResourceSession | null {
  const row = asDict(raw);
  const id = asString(row.id);
  const resourceName = asString(row.resourceName);
  if (!id || !resourceName) return null;

  const elapsed = row.elapsedSeconds;
  const amount = row.accruedAmount;

  return {
    id,
    resourceName,
    resourceType: asString(row.resourceType) ?? ResourceType.OTHER,
    status: asString(row.status) ?? ResourceSessionStatus.ACTIVE,
    startedAt: asString(row.startedAt),
    elapsedSeconds:
      typeof elapsed === 'number' && Number.isFinite(elapsed) && elapsed >= 0 ? elapsed : 0,
    // `String(...)` and never `Number(...)`: a payload that sends the amount as
    // a JSON number is still rendered from its own digits, so this client
    // cannot introduce a rounding the server never made.
    accruedAmount: typeof amount === 'number' ? String(amount) : (asString(amount) ?? '0'),
    currency: asString(row.currency) ?? '',
  };
}

/**
 * The meters running on this guest's table, or an empty list.
 *
 * An empty array is the venue's answer as much as it is an empty table: a
 * branch with the `showResourceSessions` transparency setting off gets `[]`
 * from the server, and that is the only signal this client needs. The rule is
 * deliberately NOT re-applied here — a second copy of a venue setting read from
 * a stale bootstrap can only ever disagree with the server, and this app has
 * already been bitten by exactly that (see `isAwaitingWaiterConfirmation`).
 *
 * Rejects rather than swallowing a network failure, so the caller can decide
 * between "nothing is running" and "we could not ask" — two very different
 * things to put in front of a guest.
 */
export async function loadGuestResourceSessions(): Promise<GuestResourceSession[]> {
  const { data } = await api.get(GUEST_RESOURCE_SESSIONS_PATH);
  return unwrapList<unknown>(data)
    .map(normalizeSession)
    .filter((session): session is GuestResourceSession => session !== null);
}

/** A meter that is actually counting, as opposed to one a waiter has paused. */
export function isRunning(session: Pick<GuestResourceSession, 'status'>): boolean {
  return session.status === ResourceSessionStatus.ACTIVE;
}

/**
 * What the meter reads right now: the server's own elapsed figure, plus the
 * wall-clock time since we were told it.
 *
 * The clock is advanced locally because a poll every half minute leaves the
 * number frozen in front of a guest who is watching it — the screen looks
 * broken, or worse, looks like the meter has stopped. Only TIME is extrapolated
 * here: the money is not, and must not be, because an amount this client
 * invented would disagree with the till, and a figure that disagrees with the
 * till is worse than no figure at all.
 *
 * A paused session does not advance: the venue is not charging for those
 * seconds, so counting them on the guest's phone would promise a bill that is
 * higher than the one they are handed.
 */
export function elapsedSecondsAt(
  session: GuestResourceSession,
  fetchedAt: number,
  now: number,
): number {
  if (!isRunning(session)) return session.elapsedSeconds;
  // `Math.max(0, ...)` guards a device clock that moved backwards mid-visit —
  // a timer that counts down is a support call.
  const drift = Math.max(0, Math.floor((now - fetchedAt) / 1000));
  return session.elapsedSeconds + drift;
}

/**
 * The catalogue key for a resource type, or null when there is nothing worth
 * saying.
 *
 * Only the types this app has strings for are translated, so a type added to
 * the enum server-side before this catalogue catches up shows the resource's
 * own name and no label — rather than the raw `guest.resources.type.SOMETHING`
 * that i18next renders for a key it cannot resolve, on a paying guest's phone.
 *
 * `OTHER` is absent on purpose: "Other" tells a guest nothing the resource's
 * name has not already told them.
 */
const LABELLED_TYPES: readonly string[] = [
  ResourceType.PLAYSTATION,
  ResourceType.BILLIARDS,
  ResourceType.KIDS_AREA,
  ResourceType.KARAOKE_ROOM,
  ResourceType.PRIVATE_ROOM,
  ResourceType.MEETING_ROOM,
];

export function resourceTypeLabelKey(resourceType: string): string | null {
  return LABELLED_TYPES.includes(resourceType) ? `guest.resources.type.${resourceType}` : null;
}
